import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from "@mui/material";
import { useSnackbar } from "../../../contexts/InfoContext";
import { useAuth } from "../../../contexts/AuthContext";
import updateCustomer from "../../../api/customer/update";
import getBranchMembershipPlans from "../../../api/services/prices/get_branch_membership";
import updateMembershipFee from "../../../api/customer/update_membership_fee";
import getInternationalDate from "../../../utils/getDate";

const MembershipForm = ({ open, onClose, customer, branchId }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { openSnackbar } = useSnackbar();
  const [selectedPlan, setSelectedPlan] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [plans, setPlans] = useState([]);
  const currentDate = new Date();
  const membershipExpiry = new Date(customer?.membershipExpiry);
  const isMember = customer?.membershipExpiry
    ? membershipExpiry > currentDate
    : false;

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const fetchedPlans = await getBranchMembershipPlans(branchId);
        setPlans(fetchedPlans);
      } catch (error) {
        console.error("Error fetching membership plans:", error);
        openSnackbar("Failed to load membership plans.", "error");
        setPlans([]);
      }
    };

    fetchPlans();
  }, [branchId, openSnackbar]);

  const handlePlanChange = (event) => {
    setSelectedPlan(event.target.value);
  };

  const handleExtendMembership = async () => {
    if (!selectedPlan) {
      openSnackbar("Please select a membership plan.", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      const selectedPlanData = plans.find((plan) => plan.id === selectedPlan);
      if (!selectedPlanData) {
        openSnackbar("Selected plan not found.", "error");
        return;
      }

      let activeDailySummery = "";

      const monthsToAdd = selectedPlanData.duration_months;
      const storedData = localStorage.getItem("userData");
      if (storedData) {
        const userData = JSON.parse(storedData);
        activeDailySummery = userData ? userData.activeDailySummery : "";
      }

      // Calculate new expiry date
      const baseDate =
        isMember && membershipExpiry > currentDate
          ? membershipExpiry
          : currentDate;
      const newExpiryDate = new Date(baseDate);
      newExpiryDate.setMonth(newExpiryDate.getMonth() + monthsToAdd);

      await updateCustomer(user, {
        membershipExpiry: newExpiryDate,
        blockHouse: customer.blockHouse,
      });

      await updateMembershipFee(
        user,
        {
          membershipExpiry: newExpiryDate,
          blockHouse: customer.blockHouse,
          activeDailySummery: activeDailySummery,
          date: getInternationalDate(),
          amount: selectedPlanData.price,
          branchId: branchId,
        },
        activeDailySummery
      );

      openSnackbar("Membership updated successfully!", "success");
      setSelectedPlan("");
      onClose();
    } catch (error) {
      openSnackbar(
        `Membership update failed. Please check if the daily table has been created.`,
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ backgroundColor: theme.palette.background.alt }}>
        <Typography>
          {customer?.name}, {customer?.blockHouse}
        </Typography>
      </DialogContent>
      {isMember ? (
        <>
          <DialogTitle sx={{ backgroundColor: theme.palette.background.alt }}>
            Customer Membership
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: theme.palette.background.alt }}>
            <Typography>
              This customer is already a member. Membership expires on{" "}
              {membershipExpiry.toLocaleDateString()}.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ backgroundColor: theme.palette.background.alt }}>
            <Button
              onClick={onClose}
              color="secondary"
              disabled={isSubmitting}
              sx={{
                color: theme.palette.secondary[100],
                "&:hover": {
                  backgroundColor: theme.palette.background.alt,
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle sx={{ backgroundColor: theme.palette.background.alt }}>
            Select Membership Plan
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: theme.palette.background.alt }}>
            {plans.length === 0 ? (
              <Typography>No membership plans available.</Typography>
            ) : (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel sx={{ color: theme.palette.secondary[100] }}>
                  Membership Plan
                </InputLabel>
                <Select
                  value={selectedPlan}
                  onChange={handlePlanChange}
                  label="Membership Plan"
                  disabled={isSubmitting}
                  sx={{
                    color: theme.palette.secondary[100],
                    "& .MuiSelect-icon": {
                      color: theme.palette.secondary[100],
                    },
                  }}
                >
                  {plans.map((plan) => (
                    <MenuItem key={plan.id} value={plan.id}>
                      {plan.plan_name} ({plan.duration_months}{" "}
                      {plan.duration_months === 1 ? "Month" : "Months"})
                      <Typography variant="body2" color="textSecondary">
                        {plan.price} ETB
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </DialogContent>
          <DialogActions sx={{ backgroundColor: theme.palette.background.alt }}>
            <Button
              onClick={onClose}
              color="secondary"
              disabled={isSubmitting}
              sx={{
                color: theme.palette.secondary[100],
                "&:hover": {
                  backgroundColor: theme.palette.background.alt,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExtendMembership}
              variant="contained"
              color="primary"
              disabled={!selectedPlan || isSubmitting || plans.length === 0}
              sx={{
                color: theme.palette.secondary[100],
                "&:hover": {
                  backgroundColor: theme.palette.background.alt,
                },
              }}
            >
              {isSubmitting ? "Processing..." : "Add Membership"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default MembershipForm;
